import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Loader2, Shield, ArrowLeft } from 'lucide-react';

const SecureLogin = () => {
  const navigate = useNavigate();
  const { login, verify2FA, resend2FA, requires2FA, pendingEmail } = useSecureAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA state
  const [verificationCode, setVerificationCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [resending, setResending] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        if (result.requires2FA) {
          toast.success('Verification code sent to your email');
          // Show test code in development
          if (result.testCode) {
            toast.info(`Test code: ${result.testCode}`, { duration: 10000 });
          }
        } else {
          toast.success('Welcome back!');
          navigate('/');
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await verify2FA(verificationCode, rememberDevice);
      
      if (result.success) {
        toast.success('Welcome back!');
        navigate('/');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const result = await resend2FA();
      if (result.success) {
        toast.success('New code sent!');
        if (result.testCode) {
          toast.info(`Test code: ${result.testCode}`, { duration: 10000 });
        }
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0073ea 0%, #0052a3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '2.5rem',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0073ea 0%, #0052a3 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <Shield size={32} color="#fff" />
            </div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
              Two-Step Verification
            </h1>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Enter the 6-digit code sent to<br />
              <strong>{pendingEmail}</strong>
            </p>
          </div>

          <form onSubmit={handleVerify2FA}>
            <div style={{ marginBottom: '1.5rem' }}>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                data-testid="2fa-code-input"
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '2rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  outline: 'none'
                }}
              />
            </div>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem',
              color: '#4b5563'
            }}>
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              Remember this device for 30 days
            </label>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              data-testid="verify-2fa-btn"
              style={{
                width: '100%',
                padding: '1rem',
                background: verificationCode.length === 6 ? '#0073ea' : '#e5e7eb',
                color: verificationCode.length === 6 ? '#fff' : '#9ca3af',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: verificationCode.length === 6 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : null}
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Didn't receive the code?{' '}
              <button
                onClick={handleResend}
                disabled={resending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0073ea',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {resending ? 'Sending...' : 'Resend'}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Login Form
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0073ea 0%, #0052a3 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        position: 'relative'
      }}>
        <button
          type="button"
          data-testid="back-to-home-btn"
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.875rem',
            padding: '0.25rem 0.5rem'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#0073ea'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
        >
          <ArrowLeft size={16} />
          Home
        </button>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
            Maidly
          </h1>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.25rem' }}>
            Business Manager
          </p>
        </div>

        <form onSubmit={handleLogin}>
          {/* Email */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Email
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                data-testid="login-email"
                style={{
                  width: '100%',
                  padding: '0.875rem 0.875rem 0.875rem 2.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="login-password"
                style={{
                  width: '100%',
                  padding: '0.875rem 2.75rem 0.875rem 2.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {showPassword ? <EyeOff size={18} color="#9ca3af" /> : <Eye size={18} color="#9ca3af" />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              style={{
                fontSize: '0.875rem',
                color: '#0073ea',
                textDecoration: 'none',
                fontWeight: 500
              }}
            >
              Forgot Password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            style={{
              width: '100%',
              padding: '1rem',
              background: '#0073ea',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : null}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Register Link */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Don't have an account?{' '}
            <Link
              to="/register"
              data-testid="register-link"
              style={{
                color: '#0073ea',
                fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              Sign Up
            </Link>
          </p>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
          © 2026 Maidly Business Manager
        </div>
      </div>
    </div>
  );
};

export default SecureLogin;
